// =============================================================================
// Payouts — faithful port of POS frmPayoutDashboard / frmPayout.
// List of register payouts + create new payout (detail, amount, register).
// =============================================================================
import { useState, useEffect } from 'react'
import { Plus, X, KeyRound, Pencil } from 'lucide-react'
import { listPayouts, createPayout, updatePayout, getClerkNameMaps, type Clerk } from '../services/data'
import { openCashDrawer } from '../services/hardware'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, Button, FilterBar } from '../components/shared/ui'
import ClerkPinModal from '../components/shared/ClerkPinModal'
import { useRole } from '../context/RoleContext'
import { currency, date } from '../lib/format'
import { useServerList } from '../lib/list'
import type { Payout } from '../types'
import { toast } from 'react-toastify'

const PAGE_SIZE = 25

export default function Payouts() {
  const { role } = useRole()
  const isAdmin = role === 'admin'
  const [showNew, setShowNew] = useState(false)
  const [editing, setEditing] = useState<Payout | null>(null)
  const [clerkMaps, setClerkMaps] = useState<{ byUuid: Record<string, string>; byLegacy: Record<number, string> }>({ byUuid: {}, byLegacy: {} })
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort, reload } =
    useServerList<Payout>(listPayouts, { pageSize: PAGE_SIZE, defaultSort: { column: 'issue_date', dir: 'desc' } })

  // Resolve the issuing clerk: web payouts stamp issued_from_id (auth UUID);
  // historic migrated rows only have legacy_issued_from_id.
  useEffect(() => { getClerkNameMaps().then(setClerkMaps).catch(() => {}) }, [])
  const clerkName = (p: Payout) => {
    const uuid = p.issued_from_id
    if (uuid && clerkMaps.byUuid[uuid]) return clerkMaps.byUuid[uuid]
    const legacy = p.legacy_issued_from_id
    if (legacy != null) return clerkMaps.byLegacy[legacy] ?? `Clerk #${legacy}`
    return '—'
  }

  return (
    <div>
      <PageHeader title="Payouts" subtitle={`${count.toLocaleString()} total`}
        actions={<Button onClick={() => setShowNew(true)}><Plus size={16} /> New Payout</Button>} />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search detail…" />
          <FilterBar onChange={setFilters} fields={[{ key: 'date', label: 'Date', column: 'issue_date', type: 'daterange' }]} />
        </div>
        {loading ? <Spinner label="Loading payouts…" /> : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              columns={[
                { key: 'issue_date', header: 'Date', render: p => date(p.issue_date || p.invoice_date), sortKey: 'issue_date' },
                { key: 'clerk', header: 'Clerk', render: p => clerkName(p), sortKey: 'legacy_issued_from_id' },
                { key: 'detail', header: 'Detail', render: p => p.detail || '—', sortKey: 'detail' },
                { key: 'register_number', header: 'Register', render: p => p.register_number || '—', sortKey: 'register_number' },
                { key: 'amount', header: 'Amount', render: p => currency(p.amount), className: 'text-right', sortKey: 'amount' },
                ...(isAdmin ? [{
                  key: 'actions', header: '', className: 'text-right',
                  render: (p: Payout) => (
                    <button onClick={() => setEditing(p)} className="text-gray-400 hover:text-green-700" title="Edit payout (admin)">
                      <Pencil size={15} />
                    </button>
                  ),
                }] : []),
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>
      {showNew && <PayoutForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload() }} />}
      {editing && <PayoutForm payout={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload() }} />}
    </div>
  )
}

function PayoutForm({ payout, onClose, onSaved }: { payout?: Payout; onClose: () => void; onSaved: () => void }) {
  const isEdit = Boolean(payout)
  // Parse the folded "Issued To: … | From: … | …" detail back into fields on edit.
  const parsed = (() => {
    const d = payout?.detail ?? ''
    const to = /Issued To:\s*([^|]+)/i.exec(d)?.[1]?.trim() ?? ''
    const from = /From:\s*([^|]+)/i.exec(d)?.[1]?.trim() ?? ''
    const rest = d.replace(/Issued To:\s*[^|]+\|?/i, '').replace(/From:\s*[^|]+\|?/i, '')
      .split('|').map(s => s.trim()).filter(Boolean).join(' | ')
    return { to, from, rest }
  })()
  const [issuedTo, setIssuedTo] = useState(parsed.to)
  const [issuedFrom, setIssuedFrom] = useState(parsed.from)
  const [detail, setDetail] = useState(isEdit ? parsed.rest : '')
  const [amount, setAmount] = useState(payout?.amount != null ? String(payout.amount) : '')
  const [register, setRegister] = useState(payout?.register_number ?? '')
  const [clerk, setClerk] = useState<Clerk | null>(null)
  const [clerkPromptOpen, setClerkPromptOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    const amt = parseFloat(amount)
    // Legacy frmPayout requires Issued To + Issued From and an amount.
    if (!issuedTo.trim() || !issuedFrom.trim()) { toast.error('Issued To and Issued From cannot be blank'); return }
    if (!amt) { toast.error('Amount is required'); return }
    // A verified clerk must authorize a NEW payout (legacy frmLoginPopUp). On an
    // admin edit the original clerk is preserved unless a new one is verified.
    if (!isEdit && !clerk) { setClerkPromptOpen(true); return }
    setSaving(true)
    try {
      // No name columns on payouts; fold issuer info into detail (faithful record).
      const fullDetail = `Issued To: ${issuedTo.trim()} | From: ${issuedFrom.trim()}${detail.trim() ? ` | ${detail.trim()}` : ''}`
      const fields: Partial<Payout> = {
        detail: fullDetail, amount: amt, register_number: register || null,
      }
      if (clerk) fields.issued_from_id = clerk.id
      if (isEdit && payout) {
        await updatePayout(payout.id, fields)
        toast.success('Payout updated')
      } else {
        await createPayout({
          ...fields,
          issue_date: new Date().toISOString(), invoice_date: new Date().toISOString(), is_active: true,
        })
        // Legacy WritePayOutToFile kicks the cash drawer on every payout.
        try { await openCashDrawer() } catch { /* drawer optional */ }
        toast.success('Payout has been saved')
      }
      onSaved()
    } catch (e) { console.error(e); toast.error('Failed to record payout') } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{isEdit ? 'Edit Payout' : 'New Payout'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Issued To <span className="text-red-500">*</span></label>
              <input value={issuedTo} onChange={e => setIssuedTo(e.target.value)} autoFocus
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Issued From <span className="text-red-500">*</span></label>
              <input value={issuedFrom} onChange={e => setIssuedFrom(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Detail / Reason</label>
            <input value={detail} onChange={e => setDetail(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Amount</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Register #</label>
              <input value={register} onChange={e => setRegister(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
          </div>
          {/* Authorizing clerk (stamped on payouts.issued_from_id). */}
          <button onClick={() => setClerkPromptOpen(true)}
            className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${clerk ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'}`}
            title="Clerk authorizing this payout — click to enter PIN">
            <KeyRound size={15} /> {clerk ? `Clerk: ${clerk.username}` : (isEdit ? 'Re-assign clerk (PIN)' : 'Enter Clerk PIN')}
          </button>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Record Payout'}</Button>
        </div>
      </Card>
      {clerkPromptOpen && (
        <ClerkPinModal
          title="Authorize Payout"
          onClose={() => setClerkPromptOpen(false)}
          onVerified={c => { setClerk(c); setClerkPromptOpen(false) }}
        />
      )}
    </div>
  )
}
